import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useBatches } from '../../hooks/useBatches';
import { useBatchStatusCounts } from '../../hooks/useBatchStatusCounts';
import type { BatchFilters, BatchStatusCounts } from '../../models/Batch';
import type { BatchFilterConditions } from '../components/interaction/BatchFiltersPanel';

export type BatchStatusTab = 'all' | 'active' | 'blocked' | 'expired' | 'depleted';

const DEFAULT_STATUS_COUNTS: BatchStatusCounts = { all: 0, active: 0, blocked: 0, expired: 0, depleted: 0 };

const toApiDate = (date: Date): string => format(date, 'yyyy-MM-dd');

type AdvancedFilterItem = NonNullable<BatchFilters['advanced_filters']>['filters'][number];

const buildProductFilters = (conditions: BatchFilterConditions): AdvancedFilterItem[] => {
  const productIds = conditions.productIds?.filter(Boolean) ?? [];
  if (productIds.length === 0) return [];

  return [
    {
      column: 'product_id',
      operator: 'in',
      value: productIds,
    },
  ];
};

const buildAdvancedFilters = (
  searchTerm: string,
  conditions: BatchFilterConditions,
): BatchFilters['advanced_filters'] | undefined => {
  const dateFilters: { column: string; operator: string; value: string }[] = [];

  if (conditions.receivedAt?.from) {
    dateFilters.push({ column: 'received_at', operator: 'gte', value: toApiDate(conditions.receivedAt.from) });
  }
  if (conditions.receivedAt?.to) {
    dateFilters.push({ column: 'received_at', operator: 'lte', value: toApiDate(conditions.receivedAt.to) });
  }
  if (conditions.expiryDate?.from) {
    dateFilters.push({ column: 'expiry_date', operator: 'gte', value: toApiDate(conditions.expiryDate.from) });
  }
  if (conditions.expiryDate?.to) {
    dateFilters.push({ column: 'expiry_date', operator: 'lte', value: toApiDate(conditions.expiryDate.to) });
  }

  const baseFilters = [...dateFilters, ...buildProductFilters(conditions)];
  const trimmed = searchTerm.trim();

  if (baseFilters.length > 0 && trimmed) {
    return {
      filters: [...baseFilters, { column: 'batch_number', operator: 'contains', value: trimmed }],
      logic: 'AND',
    };
  }

  if (baseFilters.length > 0) {
    return { filters: baseFilters, logic: 'AND' };
  }

  if (trimmed) {
    return {
      filters: [
        { column: 'batch_number', operator: 'contains', value: trimmed },
        { column: 'supplier_reference', operator: 'contains', value: trimmed },
      ],
      logic: 'OR',
    };
  }

  return undefined;
};

export const useBatchesMain = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<BatchStatusTab>('all');
  const [sortBy, setSortBy] = useState('received_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterConditions, setFilterConditions] = useState<BatchFilterConditions>({});

  const filters = useMemo<BatchFilters>(() => {
    const advanced_filters = buildAdvancedFilters(searchTerm, filterConditions);
    return {
      status: activeTab === 'all' ? undefined : activeTab,
      sortBy,
      sortDirection,
      ...(advanced_filters ? { advanced_filters } : {}),
    };
  }, [searchTerm, activeTab, sortBy, sortDirection, filterConditions]);

  const filtersWithoutStatus = useMemo<BatchFilters>(() => {
    const advanced_filters = buildAdvancedFilters(searchTerm, filterConditions);
    return {
      sortBy,
      sortDirection,
      ...(advanced_filters ? { advanced_filters } : {}),
    };
  }, [searchTerm, sortBy, sortDirection, filterConditions]);

  const { batches, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, total } =
    useBatches(filters);

  const { total: globalTotal } = useBatches(filtersWithoutStatus);

  const { data: statusCountsData } = useBatchStatusCounts();
  const statusCounts = statusCountsData ?? DEFAULT_STATUS_COUNTS;

  const activeFiltersCount = [
    filterConditions.receivedAt?.from,
    filterConditions.expiryDate?.from,
    (filterConditions.productIds?.length ?? 0) > 0,
  ].filter(Boolean).length;

  const sorting = useMemo(() => ({ field: sortBy, direction: sortDirection }), [sortBy, sortDirection]);

  const handleSearch = (term: string) => setSearchTerm(term);
  const handleTabChange = (tab: BatchStatusTab) => setActiveTab(tab);
  const handleFilterChange = (conditions: BatchFilterConditions) => setFilterConditions(conditions);
  const handleSortingChange = useCallback((newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
    if (!newSorting) {
      setSortBy('received_at');
      setSortDirection('desc');
      return;
    }
    setSortBy(newSorting.field);
    setSortDirection(newSorting.direction);
  }, []);

  const hasActiveFilters = searchTerm !== '' || activeTab !== 'all' || activeFiltersCount > 0;

  return {
    batches,
    isLoading,
    isFetching,
    error,
    total,
    globalTotal,
    statusCounts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    searchTerm,
    activeTab,
    sorting,
    filterConditions,
    activeFiltersCount,
    hasActiveFilters,
    handleSearch,
    handleTabChange,
    handleFilterChange,
    handleSortingChange,
  };
};
